/* Inviter un agent dont la fiche existe déjà.
 *
 * L'invitation ne crée pas la fiche — elle est déjà là, importée ou saisie.
 * Elle crée le compte de connexion et envoie le lien. Au retour du lien,
 * `session.tsx` rattache la fiche par l'adresse vérifiée du jeton.
 *
 * Le mailer intégré de Supabase plafonne à deux envois par heure : c'est peu,
 * et c'est pour cela que l'invitation se lance fiche par fiche plutôt qu'en
 * lot. Le jour où un vrai SMTP est branché, la limite tombe et on pourra
 * inviter tout le monde d'un coup.
 */

import { supabase } from "@/lib/supabase";

export class InvitationRefusee extends Error {}

export async function inviter(agent: { id: string; email: string }): Promise<void> {
  const email = agent.email.trim().toLowerCase();
  if (!email.includes("@")) throw new InvitationRefusee("Adresse invalide.");

  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: true, emailRedirectTo: window.location.origin },
  });

  if (error) {
    throw new InvitationRefusee(
      error.status === 429
        ? "Trop d'envois pour l'instant : le mailer de Supabase plafonne à deux par heure."
        : `L'invitation n'est pas partie : ${error.message}`,
    );
  }

  /* On note la date pour que l'écran puisse dire « invité le… » plutôt que de
     laisser croire qu'il ne s'est rien passé. Si ça échoue, l'invitation est
     partie quand même : on ne la rejoue pas pour autant. */
  await supabase
    .from("profils")
    .update({ invite_le: new Date().toISOString() })
    .eq("id", agent.id);
}

/** Suspendre ou réactiver une fiche. Le compte reste, l'accès tombe. */
export async function basculerActif(agent: { id: string; actif: boolean }): Promise<void> {
  const { error } = await supabase
    .from("profils")
    .update({ actif: !agent.actif })
    .eq("id", agent.id);
  if (error) throw new Error(error.message);
}
